import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { sizeLabel } from "@/lib/sizes";
import { PackageOpen, XCircle, Loader2, ShieldAlert, MessageCircle } from "lucide-react";

const STATUS_STYLE = {
  requested: "bg-slate-100 text-slate-700",
  accepted: "bg-blue-100 text-blue-700",
  picked_up: "bg-indigo-100 text-indigo-700",
  in_transit: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const DEAD_STATES = new Set(["expired", "declined", "declined_other_accepted", "withdrawn", "payment_timeout"]);
const CANCELLABLE_STATES = new Set(["requested", "accepted"]);

const DeliveryList = ({ role }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState({}); // { deliveryId: count }
  const [cancelling, setCancelling] = useState(null);   // delivery object being cancelled
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get(`/deliveries?role=${role}`);
    setItems((r.data || []).filter((d) => !DEAD_STATES.has(d.status)));
    try {
      const u = await api.get(`/chat/unread`);
      setUnread(u.data?.per_delivery || {});
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const submitCancel = async () => {
    if (!cancelling) return;
    const r = (reason || "").trim();
    if (!r) { toast.error("Please give a short reason."); return; }
    setBusy(true);
    try {
      const res = await api.post(`/parcels/${cancelling.parcel_id}/cancel`, { reason: r });
      const refund = Number(res.data?.refund_amount || 0);
      toast.success(refund > 0
        ? `Parcel cancelled. ₹${refund.toFixed(0)} refunded to your CarryIt balance.`
        : "Parcel cancelled. The carrier has been notified.");
      setCancelling(null); setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel this parcel.");
    } finally { setBusy(false); }
  };

  if (!items.length) return (
    <div className="rounded-3xl bg-white border border-slate-200 p-8 text-center text-slate-500">
      <PackageOpen className="w-8 h-8 mx-auto mb-3 text-slate-300"/>
      No {role === "sender" ? "shipments" : "requests"} yet.
    </div>
  );

  return (
    <div className="space-y-3">
      {items.map((d) => (
        <div
          key={d.id}
          data-testid={`delivery-row-${d.id}`}
          className="rounded-2xl bg-white border border-slate-200 hover:border-amber-300 hover:shadow-md transition"
        >
          <Link to={`/delivery/${d.id}`} className="block p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold truncate">{d.parcel?.pickup_location} → {d.parcel?.drop_location}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {role === "sender" ? `Carrier · ${d.carrier?.name}` : `Sender · ${d.sender?.name}`} · {sizeLabel(d.parcel)}
                </div>
                {d.status === "cancelled" && d.cancel_reason && (
                  <div className="text-[11px] text-rose-700 mt-1">
                    Cancelled by {d.cancelled_by || "someone"} · {d.cancel_reason}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  {unread[d.id] > 0 && (
                    <span
                      data-testid={`chat-unread-${d.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5"
                      title={`${unread[d.id]} unread message${unread[d.id] > 1 ? "s" : ""}`}
                    >
                      <MessageCircle className="w-3 h-3"/> {unread[d.id]}
                    </span>
                  )}
                  <Badge className={STATUS_STYLE[d.status]}>{d.status.replace("_", " ").toUpperCase()}</Badge>
                </div>
                <div className="mt-1 font-display font-extrabold text-lg">
                  ₹{role === "sender" ? d.sender_total : d.carrier_payout}
                </div>
              </div>
            </div>
          </Link>
          {role === "sender" && CANCELLABLE_STATES.has(d.status) && (
            <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between gap-3">
              <div className="text-[11px] text-slate-500">
                {d.status === "accepted"
                  ? "Carrier accepted · full refund if you cancel now"
                  : "Waiting for a carrier · cancel any time before acceptance"}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setCancelling(d); setReason(""); }}
                data-testid={`cancel-parcel-${d.id}`}
                className="h-8 rounded-full text-[11px] gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <XCircle className="w-3.5 h-3.5"/> Cancel parcel
              </Button>
            </div>
          )}
          {role === "sender" && ["in_transit", "picked_up"].includes(d.status) && (
            <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500 inline-flex items-center gap-1">
              <ShieldAlert className="w-3 h-3 text-amber-600"/>
              Already picked up — cancellation is closed. Open the delivery to raise a dispute if something is wrong.
            </div>
          )}
        </div>
      ))}

      {/* Sender cancel dialog */}
      <Dialog open={!!cancelling} onOpenChange={(v) => { if (!v) { setCancelling(null); setReason(""); } }}>
        <DialogContent data-testid="cancel-parcel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this parcel?</DialogTitle>
            <DialogDescription>
              {cancelling?.status === "accepted"
                ? "A carrier has already accepted. Cancelling will refund your payment in full and notify the carrier."
                : "No carrier has accepted yet — we'll withdraw the request and notify any carriers who saw it."}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Reason <span className="text-rose-600">*</span></label>
            <Textarea
              data-testid="cancel-parcel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., no longer needed, changed plans, sending it another way"
              rows={3}
              className="mt-1"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelling(null); setReason(""); }} disabled={busy}>Keep parcel</Button>
            <Button
              onClick={submitCancel}
              disabled={busy || !reason.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="cancel-parcel-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Cancel parcel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryList;
